/**
 * Single source of truth for AMIE log event names (EB-3798).
 *
 * @ticket EB-3798
 * Every event key emitted through {@link LoggingService} is listed here. The
 * derived {@link LogEvent} union narrows the `error/warn/info/debug/usage`
 * signatures, so a typo'd or drifted name (e.g. `phone-password.revealed` with a
 * hyphen, or `add_biling`) is a COMPILE error rather than a silently
 * un-queryable Graylog event discovered only when a dashboard goes quiet.
 * A developer adding an event adds it here first.
 *
 * Naming: `domain.event` in snake_case (single word for the domain is fine).
 * `operational` events (health/errors) ship on the operational stream via
 * error/warn/info; `usage` events (behaviour) ship via usage() at INFO.
 */
export const LOG_EVENTS = {
  // --- Operational (health / errors) -----------------------------------------
  errorHttp: 'error.http',
  errorUnhandled: 'error.unhandled',
  loggingThrottled: 'logging.throttled',
  authLoginSuccess: 'auth.login_success',
  authLogout: 'auth.logout',
  authAccessDenied: 'auth.access_denied',
  authEvent: 'auth.event',
  authOwnAccountLoadFailed: 'auth.own_account_load_failed',
  permissionsLoadFailed: 'permissions.load_failed',
  ccNumbersAmLoadFailed: 'cc_numbers.am_load_failed',
  layoutEmergency: 'layout.emergency',
  phonePasswordRevealed: 'phone_password.revealed',
  sipOrderLinkAccountLoadFailed: 'sip_order_link.account_load_failed',
  cliVerificationStatusLoadFailed: 'cli_verification.status_load_failed',
  outboundReadinessLoadFailed: 'outbound_readiness.load_failed',

  // --- Usage (behaviour) -----------------------------------------------------
  appOpen: 'app.open',
  sectionOpen: 'section.open',
  sideEntranceClick: 'side_entrance.click',
  searchQuery: 'search.query',
  searchSelect: 'search.select',
  actionInvoke: 'action.invoke',
  recordingAccess: 'recording.access',
  transcriptView: 'transcript.view',
  transcriptExport: 'transcript.export',
  transcriptFeedbackSend: 'transcript.feedback_send',
  transcriptMissingReport: 'transcript.missing_report',
  verboseLoggingToggle: 'verbose_logging.toggle',
  toastError: 'toast.error',
  tanGenerated: 'tan.generated',
  mobileConfirmationCodeSent: 'mobile_confirmation.code_sent',
  mobileConfirmationConfirmed: 'mobile_confirmation.confirmed',
  /** @ticket PS-6586 — metadata only (userAccountId, linkId); never the email or the masked recipient. */
  sipOrderLinkSent: 'sip_order_link.sent',
} as const;

/** Union of every valid log event name — narrows the LoggingService signatures. */
export type LogEvent = (typeof LOG_EVENTS)[keyof typeof LOG_EVENTS];
